import { AlertCircle, Flag, Plus, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { NonProdAdvisoryBanner } from "@/common/NonProdAdvisoryBanner";
import { Button } from "@/common/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/common/ui/card";
import { Skeleton } from "@/common/ui/skeleton";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/common/ui/table";

import { CreateFlagDialog } from "./CreateFlagDialog";
import { DeleteFlagDialog } from "./DeleteFlagDialog";
import { EditFlagDialog, type ServeValueEdit } from "./EditFlagDialog";
import { createRule, fetchFlagWithRules, updateRule } from "./feature-flags-api";
import type { TargetingRule } from "./feature-flags-types";
import type { CreateFlagInput, UpdateFlagInput } from "./feature-flags-types";
import { FlagRow } from "./FlagRow";
import { setServeValue } from "./set-serve-value";
import { useFeatureFlags } from "./use-feature-flags";

export function FeatureFlagsPage(): React.ReactElement {
  const { flags, isLoading, error, refresh, create, toggleEnv, update, remove } = useFeatureFlags();
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<string | null>(null);

  const handleCreate = useCallback(
    async (input: CreateFlagInput) => {
      await create(input);
      setShowCreate(false);
    },
    [create]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    await remove(deleteTarget);
    setDeleteTarget(null);
  }, [deleteTarget, remove]);

  const handleToggleEnv = useCallback(
    (key: string, environment: string, enabled: boolean) => {
      void toggleEnv(key, environment, enabled);
    },
    [toggleEnv]
  );

  /* The list endpoint returns flags without their rules, so the dialog cannot
     tell an editable serve-rule from real targeting until they are fetched.
     `null` means "still loading", which the dialog shows rather than guessing. */
  const [editingRules, setEditingRules] = useState<TargetingRule[] | null>(null);
  const [rulesError, setRulesError] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inFlightKeyRef = useRef<string | null>(null);

  useEffect(() => {
    setEditingRules(null);
    setRulesError(false);
    setSaveError(null);
    setSaving(false);
    // Abandons any in-flight save's right to report: it belonged to the flag
    // that was open when it started, not to this one.
    inFlightKeyRef.current = editTarget;
    if (!editTarget) return;

    let cancelled = false;
    /* Wrapped so a SYNCHRONOUS throw from the client is caught too. A bare
       `fetchFlagWithRules(...).then(...)` throws inside the effect if the call
       fails before returning a promise, which takes the whole page down rather
       than the one dialog. */
    void (async () => {
      try {
        const data = await fetchFlagWithRules(editTarget);
        if (!cancelled) setEditingRules(data.rules);
      } catch {
        // Say so rather than leaving the dialog on "Loading rules..." forever.
        // Never fall back to an empty list: that reads as "no rules", which
        // would invite creating one on a flag that may already have targeting.
        if (!cancelled) setRulesError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editTarget]);

  const handleEditConfirm = useCallback(
    async (key: string, input: UpdateFlagInput, serve: ServeValueEdit | null) => {
      setSaveError(null);
      setSaving(true);
      /* Which flag this save belongs to. Every write below happens after an
         await, and the operator can have moved on by then, so each one checks
         that the dialog is still showing this flag before reporting against it.
         Otherwise a slow failure lands its message on whichever flag was opened
         next. */
      inFlightKeyRef.current = key;
      const stillEditing = (): boolean => inFlightKeyRef.current === key;

      /* The flag save has to land BEFORE the rule write. `update` reports
         failure rather than throwing, so without checking it a rejected save
         would still change targeting on a flag the operator was just told did
         not save. */
      const saved = await update(key, input);
      if (!saved.ok) {
        // The API's own message, not a generic string: the page banner carries
        // it too, but that banner is behind this dialog's overlay.
        if (stillEditing()) {
          setSaveError(saved.message);
          setSaving(false);
        }
        return;
      }

      if (serve && editingRules !== null) {
        try {
          await setServeValue(
            { flagKey: key, rules: editingRules, ...serve },
            { createRule, updateRule }
          );
        } catch (e) {
          // Surfaced in the DIALOG, not the page banner: the banner sits behind
          // the overlay, so an error reported only there is never seen.
          if (stillEditing()) {
            setSaveError(e instanceof Error ? e.message : "Could not save the serve rule.");
            setSaving(false);
          }
          return;
        }
      }
      if (!stillEditing()) return;
      setSaving(false);
      setEditTarget(null);
    },
    [update, editingRules]
  );

  const editingFlag = editTarget ? (flags.find((f) => f.flagKey === editTarget) ?? null) : null;

  return (
    <div data-testid="feature-flags-page" className="space-y-6">
      <NonProdAdvisoryBanner
        dismissKey="feature-flags"
        prodUrl="https://ops.letsendure.com/feature-flags"
        surfaceName="Feature flags"
        renderBody={(env) => (
          <p>
            This <span className="font-mono uppercase">{env}</span> build is a sandbox for
            exercising the feature-flags system itself; toggles here have no effect on real
            consumers unless one is deliberately pointed at the{" "}
            <span className="font-mono lowercase">{env}-test</span> environment.
          </p>
        )}
      />

      <div className="flex items-center justify-end gap-2">
        <Button data-testid="refresh-flags-button" variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
        <Button data-testid="create-flag-button" size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Flag
        </Button>
      </div>

      {error && (
        <div
          data-testid="flags-error"
          className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
          <Button variant="outline" size="sm" className="ml-auto" onClick={refresh}>
            Retry
          </Button>
        </div>
      )}

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {!isLoading && flags.length === 0 && !error && (
        <Card data-testid="empty-flags-card" className="mx-auto max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Flag className="h-8 w-8 text-primary" />
            </div>
            <CardTitle>No Feature Flags</CardTitle>
            <CardDescription>
              Create your first feature flag to start controlling features across environments.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button data-testid="empty-create-flag-button" onClick={() => setShowCreate(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Flag
            </Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && flags.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Environments</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flags.map((flag) => (
                  <FlagRow
                    key={flag.flagKey}
                    flag={flag}
                    onToggleEnv={handleToggleEnv}
                    onEdit={setEditTarget}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <CreateFlagDialog
        open={showCreate}
        onConfirm={handleCreate}
        onCancel={() => setShowCreate(false)}
      />

      <EditFlagDialog
        flag={editingFlag}
        rules={editingRules}
        rulesError={rulesError}
        saveError={saveError}
        saving={saving}
        open={editTarget !== null}
        onConfirm={(key, input, serve) => {
          // Caught here too: an async handler passed straight to a prop drops
          // its rejection, which is an unhandled rejection and a dialog that
          // silently does nothing.
          void handleEditConfirm(key, input, serve).catch((e: unknown) => {
            setSaveError(e instanceof Error ? e.message : "Could not save the flag.");
          });
        }}
        onCancel={() => setEditTarget(null)}
      />

      <DeleteFlagDialog
        flagKey={deleteTarget}
        open={deleteTarget !== null}
        onConfirm={() => void handleDeleteConfirm()}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
